/**
 * Runtime configuration.
 *
 * The published `web` container image is environment-agnostic: it must not bake
 * the API URL (or any deployment-specific value) into the static bundle at build
 * time. Instead, the container serves `/config.js` at startup, which sets
 * `window.__APP_CONFIG__` from environment variables before the app bundle runs.
 *
 * Resolution order for each value:
 *   1. `window.__APP_CONFIG__` (injected at runtime by `/config.js`)
 *   2. `import.meta.env.VITE_*` (local dev via Vite)
 *   3. empty / undefined (callers fall back to `window.location.origin`)
 */

export interface AppRuntimeConfig {
  apiUrl?: string;
  stage?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppRuntimeConfig;
  }
}

function readRuntimeConfig(): AppRuntimeConfig {
  if (typeof window !== "undefined" && window.__APP_CONFIG__) {
    return window.__APP_CONFIG__;
  }
  return {};
}

/**
 * Configured API base URL with any trailing slash removed. Returns an empty
 * string when not configured, so callers can fall back to
 * `window.location.origin` (same-origin deployment).
 */
export function getApiBaseUrl(): string {
  const runtime = readRuntimeConfig().apiUrl;
  const fromEnv = import.meta.env.VITE_API_URL;
  const raw = (runtime ?? fromEnv ?? "").trim();
  return raw.replace(/\/$/, "");
}

/** Deployment stage label, if configured. */
export function getStage(): string | undefined {
  const runtime = readRuntimeConfig().stage;
  const fromEnv = import.meta.env.VITE_STAGE;
  const value = (runtime ?? fromEnv ?? "").trim();
  return value === "" ? undefined : value;
}
